
"use client";

import React, { createContext, useContext, useEffect, useMemo, useState, useRef } from 'react';
import type { FirebaseApp } from 'firebase/app';
import { onAuthStateChanged, type User, type Auth, signInAnonymously } from 'firebase/auth';
import type { Firestore } from 'firebase/firestore';

import { initializeFirebase } from "."; 

// This single context will hold both the Firebase instances and the user state.
type FirebaseClientContextValue = {
  app: FirebaseApp | null;
  auth: Auth | null;
  firestore: Firestore | null;
  user: User | null;
  loading: boolean; // True until onAuthStateChanged makes its first determination
};

const FirebaseClientContext = createContext<FirebaseClientContextValue | undefined>(undefined);

export function FirebaseClientProvider({ children }: { children: React.ReactNode }) {
  const firebaseInstances = useMemo(() => initializeFirebase(), []);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!firebaseInstances.auth) return;

    const unsubscribe = onAuthStateChanged(firebaseInstances.auth, (user) => {
      setUser(user);
      setLoading(false);
    });

    // Cleanup the listener on unmount
    return () => unsubscribe();
  }, [firebaseInstances.auth]);

  const value = useMemo(() => ({
    app: firebaseInstances.app,
    auth: firebaseInstances.auth,
    firestore: firebaseInstances.firestore,
    user,
    loading
  }), [firebaseInstances, user, loading]);

  return (
    <FirebaseClientContext.Provider value={value}>
      {children}
    </FirebaseClientContext.Provider>
  );
}

// Hook to access the entire context value
export const useFirebaseClient = () => {
    const context = useContext(FirebaseClientContext);
    if (context === undefined) {
        throw new Error("useFirebaseClient must be used within a FirebaseClientProvider");
    }
    return context;
}
